import {
  calculateAutomaticSplit,
  effectiveSplit,
  isOrderFullyDone,
  type Order,
  type OrderItem,
} from "../shared/order-domain";
import {
  isFileDownloaded,
  isItemAcknowledged,
  markDownloadedLocally,
  unacknowledgedItems,
} from "./downloads";
import { isFriend, isOwner, state } from "./state";
import {
  DASH,
  element,
  escapeHtml,
  fileUrl,
  formatDateTime,
  formatMoney,
  pickupDateHtml,
} from "./ui";

const ICON_DOWNLOAD = '<svg class="file-link-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>';
const ICON_EDIT = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>';
const ICON_DELETE = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>';
const ICON_INFO = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>';

const OWNER_HEADER = `
  <th class="col-pickup"><div class="pickup-head"><span>取件码</span><span class="pickup-th-date">取件日期</span></div></th>
  <th class="col-file">文件</th>
  <th class="col-color">颜色</th>
  <th class="col-qty">数量</th>
  <th class="col-print">打印完成</th>
  <th>分成</th>
  <th></th>`;

const FRIEND_HEADER = `
  <th class="col-pickup-time">取件时间 / 取件码</th>
  <th class="col-combined">文件 / 数量 / 颜色</th>
  <th class="col-pt-print">打印完成</th>
  <th class="col-split">分成</th>
  <th class="col-detail">详细信息</th>`;

interface EmptyItem {
  _empty: true;
}

type DisplayItem = OrderItem | EmptyItem;

function isEmptyItem(item: DisplayItem): item is EmptyItem {
  return "_empty" in item;
}

export function visibleOrders(): Order[] {
  let orders = state.orders;
  if (state.filter === "unprinted") {
    orders = orders.filter((order) => order.items.some((item) => !item.is_printed));
  }
  if (state.filter === "untransferred") {
    orders = orders.filter((order) => !order.is_transferred);
  }
  const query = state.search.trim().toLowerCase();
  if (!query) return orders;
  return orders.filter((order) =>
    [order.xianyu_id, order.pickup_code, order.notes]
      .concat(order.items.map((item) => item.file_name))
      .concat(order.items.map((item) => item.color))
      .some((value) => String(value ?? "").toLowerCase().includes(query)),
  );
}

function splitMoneyHtml(order: Order): string {
  const actual = effectiveSplit(order);
  if (order.split_special !== null) {
    return `<div class="split-big split-main">¥${formatMoney(actual)}</div><div class="split-auto-strike">自动 ¥${formatMoney(calculateAutomaticSplit(order.total_price))}</div>`;
  }
  return `<div class="split-big split-main">¥${formatMoney(actual)}</div>`;
}

function transferBadge(order: Order): string {
  return `<span class="pill ${order.is_transferred ? "pill-green" : "pill-orange"} transfer-badge">${order.is_transferred ? "已转" : "未转"}</span>`;
}

function colorHtml(color: string | null): string {
  if (!color) return DASH;
  return `<span class="color-chip${color.includes("白") ? " color-white" : ""}">${escapeHtml(color)}</span>`;
}

function downloadStatusHtml(item: OrderItem): string {
  if (!item.file_key) return "";
  if (item.downloaded_at) {
    return `<span class="pill pill-green pill-sm file-dl-badge" title="打印端已下载于 ${formatDateTime(item.downloaded_at)}">已下载</span>`;
  }
  if (item.acknowledged_at) {
    return '<span class="pill pill-green pill-sm file-dl-badge" title="打印端已确认，无下载回执">已读</span>';
  }
  return '<span class="pill pill-orange pill-sm file-dl-badge" title="打印端尚未处理">未读</span>';
}

function fileLinkHtml(item: OrderItem): string {
  if (!item.file_key) {
    return item.file_name
      ? `<span class="file-chip file-uploading" title="${escapeHtml(item.file_name)}"><span class="spin"></span>${escapeHtml(item.file_name)} · 上传中</span>`
      : DASH;
  }
  const name = escapeHtml(item.file_name || "文件");
  if (!isFriend()) {
    return `<span class="file-cell-wrap"><span class="file-plain" title="${name}"><span class="file-plain-name">${name}</span></span>${downloadStatusHtml(item)}</span>`;
  }
  const badge = isFileDownloaded(item)
    ? ""
    : '<span class="pill pill-orange pill-sm file-new-badge">未下载</span>';
  return `<span class="file-cell-wrap"><a class="file-link" href="${fileUrl(item.file_key)}" download data-file-key="${escapeHtml(item.file_key)}" title="下载 ${name}"><span class="file-link-name">${name}</span>${ICON_DOWNLOAD}</a>${badge}</span>`;
}

function printHtml(item: DisplayItem): string {
  if (isEmptyItem(item)) return "";
  if (isFriend()) {
    return `<input type="checkbox" class="status-check" data-item="${item.id}" aria-label="标记 ${escapeHtml(item.file_name || "文件")} 已打印" ${item.is_printed ? "checked" : ""}>`;
  }
  return item.is_printed
    ? '<span class="print-done" title="已打印">✓</span>'
    : '<span class="muted">—</span>';
}

function ownerRows(orders: readonly Order[], done: boolean): string[] {
  const rows: string[] = [];
  for (const order of orders) {
    const items: DisplayItem[] = order.items.length ? order.items : [{ _empty: true }];
    items.forEach((item, index) => {
      let cells = "";
      if (index === 0) {
        cells += `<td rowspan="${items.length}" class="col-pickup"><div class="pickup-inner"><div class="pickup-head"><div class="pickup-code mono">${escapeHtml(order.pickup_code) || DASH}</div>${pickupDateHtml(order.pickup_time, order.order_time)}</div><div class="pickup-sub"><span class="sub-date mono">${order.order_time ? formatDateTime(order.order_time) : ""}</span><span class="sub-sep">·</span><span class="sub-id" title="${escapeHtml(order.xianyu_id)}">${escapeHtml(order.xianyu_id)}</span></div></div></td>`;
      }
      cells += `<td class="col-file">${isEmptyItem(item) ? DASH : fileLinkHtml(item)}</td>`;
      cells += `<td class="col-color">${isEmptyItem(item) ? "" : colorHtml(item.color)}</td>`;
      cells += `<td class="col-qty">${isEmptyItem(item) ? "" : `<span class="qty-val">${item.quantity}</span>`}</td>`;
      cells += `<td class="col-print">${printHtml(item)}</td>`;
      if (index === 0) {
        const total = order.total_price === null ? "—" : `¥${order.total_price.toFixed(2)}`;
        cells += `<td rowspan="${items.length}" class="col-money"><div class="money-wrap"><div>${splitMoneyHtml(order)}<div class="total-sub">总价 ${total}</div></div>${transferBadge(order)}</div></td>`;
        cells += `<td rowspan="${items.length}" class="col-actions">${isOwner() ? `<div class="row-actions"><button class="icon-btn" data-act="edit" title="编辑" aria-label="编辑">${ICON_EDIT}</button><button class="icon-btn danger" data-act="del" title="删除" aria-label="删除">${ICON_DELETE}</button></div>` : ""}</td>`;
      }
      rows.push(`<tr data-id="${order.id}" class="${index === 0 ? "order-start" : "order-cont"}${done ? " is-done" : ""}">${cells}</tr>`);
    });
  }
  return rows;
}

function orderHasUndownloaded(order: Order): boolean {
  return order.items.some((item) => Boolean(item.file_key) && !isFileDownloaded(item));
}

function friendFileHtml(item: DisplayItem): string {
  if (isEmptyItem(item)) return DASH;
  if (!item.file_key) {
    return item.file_name
      ? `<span class="file-chip file-uploading" title="${escapeHtml(item.file_name)}"><span class="spin"></span>${escapeHtml(item.file_name)} · 上传中</span>`
      : DASH;
  }
  const name = escapeHtml(item.file_name || "文件");
  return `<a class="file-link" href="${fileUrl(item.file_key)}" download data-file-key="${escapeHtml(item.file_key)}" title="下载 ${name}"><span class="file-link-name">${name}</span>${ICON_DOWNLOAD}</a>`;
}

function friendItemHtml(item: DisplayItem): string {
  if (isEmptyItem(item)) return `<div class="pt-unit pt-group-empty">${DASH}</div>`;
  return `<div class="pt-unit"><div class="pt-file-line">${friendFileHtml(item)}</div><div class="pt-meta">${colorHtml(item.color)}<span class="pt-qty">× ${item.quantity}</span></div></div>`;
}

function orderPrintHtml(order: Order): string {
  const printed = order.items.length > 0 && order.items.every((item) => item.is_printed);
  return `<label class="pt-donecheck"><input type="checkbox" class="order-print-check" data-order="${order.id}" aria-label="标记整单打印完成"${printed ? " checked" : ""}></label>`;
}

function friendRows(orders: readonly Order[], done: boolean): string[] {
  return orders.map((order) => {
    const items: DisplayItem[] = order.items.length ? order.items : [{ _empty: true }];
    const total = order.total_price === null ? "—" : `¥${order.total_price.toFixed(2)}`;
    return `<tr data-id="${order.id}" class="order-start${done ? " is-done" : ""}${orderHasUndownloaded(order) ? " pt-undownloaded" : ""}"><td class="col-pickup-time"><div class="pt-time">${pickupDateHtml(order.pickup_time, order.order_time)}</div><div class="pt-code mono">${escapeHtml(order.pickup_code) || DASH}</div></td><td class="col-combined"><div class="pt-groups">${items.map(friendItemHtml).join("")}</div></td><td class="col-pt-print">${orderPrintHtml(order)}</td><td class="col-split"><div class="pt-split"><div class="split-big split-main">¥${formatMoney(effectiveSplit(order))}</div><div class="total-sub">总价 ${total}</div></div></td><td class="col-detail"><button type="button" class="icon-btn detail-btn" data-act="detail" title="订单详情" aria-label="订单详情">${ICON_INFO}</button></td></tr>`;
  });
}

function applyTableHeaders(): void {
  document
    .querySelectorAll<HTMLTableRowElement>("#activePanel thead tr, #donePanel thead tr")
    .forEach((row) => {
      row.innerHTML = isFriend() ? FRIEND_HEADER : OWNER_HEADER;
    });
}

function cardHtml(order: Order, done: boolean): string {
  const items = order.items.length
    ? order.items
        .map(
          (item) => `<div class="oc-item"><div class="oc-item-file">${fileLinkHtml(item)}</div><div class="oc-item-meta">${colorHtml(item.color)}<span class="oc-qty">×${item.quantity}</span><label class="oc-print">${isFriend() ? `<input type="checkbox" class="status-check" data-item="${item.id}" aria-label="标记 ${escapeHtml(item.file_name || "文件")} 已打印" ${item.is_printed ? "checked" : ""}><span>已打印</span>` : `<span>${item.is_printed ? "已打印 ✓" : "未打印"}</span>`}</label></div></div>`,
        )
        .join("")
    : '<div class="muted">无文件</div>';
  const total = order.total_price === null ? "—" : `¥${order.total_price.toFixed(2)}`;
  return `<article class="order-card${done ? " is-done" : ""}" data-id="${order.id}"><div class="oc-head"><div><div class="oc-pickup"><span class="pickup-code mono">${escapeHtml(order.pickup_code) || "—"}</span> ${pickupDateHtml(order.pickup_time, order.order_time)}</div>${isOwner() ? `<div class="pickup-sub"><span class="sub-date mono">${order.order_time ? formatDateTime(order.order_time) : ""}</span><span class="sub-sep">·</span><span class="sub-id">${escapeHtml(order.xianyu_id)}</span></div>` : ""}</div><div class="row-actions">${isOwner() ? `<button class="icon-btn" data-act="edit" title="编辑" aria-label="编辑">${ICON_EDIT}</button><button class="icon-btn danger" data-act="del" title="删除" aria-label="删除">${ICON_DELETE}</button>` : ""}</div></div><div class="oc-items">${items}</div><div class="oc-foot"><div>${splitMoneyHtml(order)}<div class="total-sub">总价 ${total}</div></div>${transferBadge(order)}</div></article>`;
}

function renderCards(active: readonly Order[], done: readonly Order[]): void {
  element<HTMLElement>("ordersCardsActive").innerHTML = active.map((order) => cardHtml(order, false)).join("");
  element<HTMLElement>("ordersCardsDone").innerHTML = done.map((order) => cardHtml(order, true)).join("");
}

export function updateOrdersLayout(): void {
  const spacer = element<HTMLElement>("ordersSpacer");
  const activePanel = element<HTMLElement>("activePanel");
  const donePanel = element<HTMLElement>("donePanel");
  if (activePanel.hidden || donePanel.hidden) {
    spacer.hidden = true;
    spacer.style.minHeight = "";
  } else {
    const needed = Math.max(
      0,
      window.innerHeight - activePanel.getBoundingClientRect().top - activePanel.offsetHeight - 16,
    );
    spacer.hidden = needed === 0;
    spacer.style.minHeight = needed ? `${needed}px` : "";
  }
  const hint = element<HTMLButtonElement>("scrollHint");
  hint.hidden = donePanel.hidden || donePanel.getBoundingClientRect().top <= window.innerHeight * 0.88;
}

export function updateAcknowledgementButton(): void {
  const button = element<HTMLButtonElement>("markAllReadBtn");
  button.hidden = !isFriend();
  if (!isFriend()) return;
  const count = unacknowledgedItems(state.orders).length;
  button.disabled = count === 0;
  button.textContent = count ? `✓ 全部标为已读（${count}）` : "✓ 已全部读完";
}

export function renderOrders(): void {
  applyTableHeaders();
  const orders = visibleOrders();
  const active = orders.filter((order) => !isOrderFullyDone(order));
  const done = orders.filter((order) => isOrderFullyDone(order));
  element<HTMLElement>("emptyHint").hidden = state.orders.length > 0;
  element<HTMLElement>("filterEmptyHint").hidden =
    state.orders.length === 0 || orders.length > 0 || state.loading;
  const activePanel = element<HTMLElement>("activePanel");
  const donePanel = element<HTMLElement>("donePanel");
  activePanel.hidden = active.length === 0;
  donePanel.hidden = done.length === 0;
  element<HTMLElement>("ordersMain").classList.toggle(
    "has-done-pending",
    active.length > 0 && done.length > 0,
  );
  element<HTMLTableSectionElement>("ordersBodyActive").innerHTML =
    (isFriend() ? friendRows(active, false) : ownerRows(active, false)).join("");
  element<HTMLTableSectionElement>("ordersBodyDone").innerHTML =
    (isFriend() ? friendRows(done, true) : ownerRows(done, true)).join("");
  renderCards(active, done);
  updateAcknowledgementButton();
  window.requestAnimationFrame(updateOrdersLayout);
}

export function updateItemInState(item: OrderItem): void {
  for (const order of state.orders) {
    const index = order.items.findIndex((candidate) => candidate.id === item.id);
    if (index >= 0) {
      order.items[index] = item;
      return;
    }
  }
}

export function updateOrderInState(order: Order): void {
  const index = state.orders.findIndex((candidate) => candidate.id === order.id);
  if (index >= 0) state.orders[index] = order;
}

export function orderFromEventTarget(target: EventTarget | null): Order | null {
  const host = target instanceof Element ? target.closest<HTMLElement>("[data-id]") : null;
  if (!host) return null;
  return state.orders.find((order) => String(order.id) === host.dataset.id) ?? null;
}

export function recordFileDownload(event: MouseEvent): void {
  const link = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>("[data-file-key]") : null;
  if (!link?.dataset.fileKey || !isFriend()) return;
  markDownloadedLocally(link.dataset.fileKey);
  link.closest(".file-cell-wrap")?.querySelector(".file-new-badge")?.remove();
  const row = link.closest<HTMLElement>("[data-id]");
  const order = orderFromEventTarget(link);
  if (row && order) row.classList.toggle("pt-undownloaded", orderHasUndownloaded(order));
}

export function openOrderDetail(order: Order): void {
  const rows = order.items.length
    ? order.items
        .map((item) => {
          const download = isFileDownloaded(item)
            ? "已下载"
            : isItemAcknowledged(item)
              ? "已读未下载"
              : "未下载";
          return `<tr><td>${escapeHtml(item.file_name) || "—"}</td><td>${escapeHtml(item.color) || "—"}</td><td class="detail-num">×${item.quantity}</td><td>${item.is_printed ? "已打印" : "未打印"}</td><td>${download}</td></tr>`;
        })
        .join("")
    : '<tr><td colspan="5" class="muted">无文件</td></tr>';
  const special = order.split_special !== null
    ? `（特殊，自动为 ¥${formatMoney(calculateAutomaticSplit(order.total_price))}）`
    : "（自动算）";
  element<HTMLElement>("detailContent").innerHTML = `<dl class="detail-list"><div><dt>取件码</dt><dd class="mono">${escapeHtml(order.pickup_code) || "—"}</dd></div><div><dt>取件时间</dt><dd>${escapeHtml(order.pickup_time) || "—"}</dd></div><div><dt>闲鱼ID</dt><dd>${escapeHtml(order.xianyu_id) || "—"}</dd></div><div><dt>下单时间</dt><dd>${order.order_time ? formatDateTime(order.order_time) : "—"}</dd></div></dl><div class="detail-section"><div class="detail-h">文件（共 ${order.items.length} 个）</div><table class="detail-table"><thead><tr><th>文件</th><th>颜色</th><th>数量</th><th>打印</th><th>下载</th></tr></thead><tbody>${rows}</tbody></table></div><dl class="detail-list"><div><dt>总价</dt><dd>¥${formatMoney(order.total_price)}</dd></div><div><dt>分成</dt><dd>¥${formatMoney(effectiveSplit(order))} <span class="muted">${special}</span></dd></div><div><dt>是否转账</dt><dd>${order.is_transferred ? "已转账" : "未转账"}</dd></div>${order.notes ? `<div><dt>备注</dt><dd>${escapeHtml(order.notes)}</dd></div>` : ""}</dl>`;
}
